import React from "react";
import {
  Box,
  Container,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Avatar,
  Paper,
  Divider,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  Collapse,
  Tooltip,
} from "@mui/material";
import {
  Download,
  Delete,
  ExpandMore as ExpandMoreIcon,
  PictureAsPdf as PdfIcon,
  Description as XmlIcon,
} from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useState } from "react";
import services from "src/services/Services";
import WidgetCustomLoader from "src/widgets/WidgetCustomLoader";
import WidgetTable from "src/widgets/WidgetTable";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

const DEFAULT_INICIO = dayjs().subtract(20, "day");
const DEFAULT_FIN = dayjs();

const Orders = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ open: false, message: "", severity: "info" });

  const [openModal, setOpenModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [search, setSearch] = useState("");
  const [fechaInicio, setFechaInicio] = useState(DEFAULT_INICIO);
  const [fechaFin, setFechaFin] = useState(DEFAULT_FIN);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [expanded, setExpanded] = useState({});

  const fetchData = useCallback(async (payload) => {
  try {
    setLoading(true);
    const response = await services.getPedidos(payload);

    console.log("Response completo:", JSON.stringify(response));

    const rows = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response?.data?.rows)
      ? response.data.rows
      : [];

    console.log("Rows resultado:", rows);

    setData(rows);
  } catch (error) {
    console.error("Error fetching pedidos:", error);
    setData([]);
  } finally {
    setLoading(false);
  }
}, []);

  useEffect(() => {
    fetchData({
      search: "",
      fechaInicio: DEFAULT_INICIO.format("YYYY-MM-DD"),
      fechaFin: DEFAULT_FIN.format("YYYY-MM-DD"),
    });
  }, []);

  const handleOpenModal = (row) => {
    setSelectedOrder(row);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedOrder(null);
  };

  const handleBuscar = () => {
    if (search.trim() !== "") {
      fetchData({
        search,
        fechaInicio: null,
        fechaFin: null,
      });
      setPage(0);
      return;
    }

    const inicio = fechaInicio ?? DEFAULT_INICIO;
    const fin = fechaFin ?? DEFAULT_FIN;

    fetchData({
      search: "",
      fechaInicio: inicio.format("YYYY-MM-DD"),
      fechaFin: fin.format("YYYY-MM-DD"),
    });
    setPage(0);
  };

  const handleFechaInicioChange = (value) => {
    setSearch("");
    setFechaInicio(value);
  };

  const handleFechaFinChange = (value) => {
    setSearch("");
    setFechaFin(value);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);

    if (value.trim() !== "") {
      setFechaInicio(null);
      setFechaFin(null);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      handleBuscar();
    }
  };

  const formatCurrency = (num) =>
    num != null ? Number(num).toLocaleString("es-MX", { style: "currency", currency: "MXN" }) : "-";

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString("es-MX") : "-");

  const handleDownload = (base64OrUrl, filename, mimeType) => {
    if (!base64OrUrl) return;

    if (typeof base64OrUrl === "string" && (base64OrUrl.startsWith("http") || base64OrUrl.startsWith("/"))) {
      const link = document.createElement("a");
      link.href = base64OrUrl;
      link.download = filename || "";
      document.body.appendChild(link);
      link.click();
      link.remove();
      return;
    }

    try {
      const byteCharacters = atob(base64OrUrl);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || "";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error descargando archivo:", err);
    }
  };

  const handleDelete = async (folio) => {
    try {
      const body = { Folio: folio };
      await services.deleteGuia(body);
      fetchData({
        search: "",
        fechaInicio: (fechaInicio ?? DEFAULT_INICIO).format("YYYY-MM-DD"),
        fechaFin: (fechaFin ?? DEFAULT_FIN).format("YYYY-MM-DD"),
      });
      setAlert({ open: true, message: "Guía eliminada correctamente", severity: "success" });
    } catch (error) {
      console.error(error);
      setAlert({ open: true, message: "Error al eliminar el pedido", severity: "error" });
    }
  };

  const visibleRows = useMemo(() => data || [], [data]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleExpand = (folio) => {
    setExpanded((prev) => ({ ...prev, [folio]: !prev[folio] }));
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      {alert.open && (
        <Alert
          severity={alert.severity}
          onClose={() => setAlert({ ...alert, open: false })}
          sx={{ mb: 2, borderRadius: 1 }}
        >
          {alert.message}
        </Alert>
      )}

      <Paper elevation={0} sx={{ mb: 2, p: 2, borderRadius: 3, background: "linear-gradient(180deg,#fff,#fbfdff)" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar sx={{ bgcolor: "transparent", width: 48, height: 48 }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="4" width="18" height="4" rx="1.5" fill="#556cd6" />
              <rect x="3" y="10" width="18" height="10" rx="1.5" stroke="#1976d2" strokeWidth="1.2" />
            </svg>
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Pedidos
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={5}>
          <TextField
            fullWidth
            size="small"
            label="Buscar por Orden o Referencia"
            value={search}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <DatePicker label="Fecha inicio" value={fechaInicio} onChange={handleFechaInicioChange} disabled={Boolean(search)} />
        </Grid>

        <Grid item xs={12} md={3}>
          <DatePicker label="Fecha fin" value={fechaFin} onChange={handleFechaFinChange} disabled={Boolean(search)} maxDate={dayjs()} />
        </Grid>

        <Grid item xs={12} md={1}>
          <Button fullWidth variant="contained" onClick={handleBuscar}>
            Buscar
          </Button>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {loading ? (
        <WidgetCustomLoader />
      ) : (
        <>
          <Typography variant="h4" sx={{ mb: 2 }}>
            Pedidos
          </Typography>

          <Paper elevation={1} sx={{ borderRadius: 3, overflow: "hidden", boxShadow: "0 12px 36px rgba(16,24,40,0.06)" }}>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Folio</TableCell>
                    <TableCell>Fecha emisión</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Sucursal</TableCell>
                    <TableCell>Estatus</TableCell>
                    <TableCell align="right">Importe</TableCell>
                    <TableCell align="right">Impuestos</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Carrier</TableCell>
                    <TableCell>Paquetería</TableCell>
                    <TableCell>UUID</TableCell>
                    <TableCell>Envío</TableCell>
                    <TableCell>Llegada</TableCell>
                    <TableCell>Recibió</TableCell>
                    <TableCell>Archivos</TableCell>
                    <TableCell>Eliminar</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {visibleRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                    <React.Fragment key={row.FolioVanti || JSON.stringify(row).slice(0, 20)}>
                      <TableRow hover>
                        <TableCell padding="checkbox">
                          <Tooltip title={expanded[row.FolioVanti] ? "Cerrar" : "Abrir detalle"}>
                            <IconButton
                              size="small"
                              onClick={() => toggleExpand(row.FolioVanti)}
                              sx={{
                                transform: expanded[row.FolioVanti] ? "rotate(180deg)" : "rotate(0deg)",
                                transition: "transform 200ms",
                              }}
                            >
                              <ExpandMoreIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>

                        <TableCell>{row.FolioVanti}</TableCell>
                        <TableCell>{row.FechaEmision ?? "-"}</TableCell>
                        <TableCell>{row.Cliente ?? "-"}</TableCell>
                        <TableCell>{row.ClienteSucursal ?? "-"}</TableCell>
                        <TableCell>{row.Estatus ?? "-"}</TableCell>
                        <TableCell align="right">{formatCurrency(row.Importe)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.Impuestos)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.Total)}</TableCell>
                        <TableCell>{row.Carrier ?? "-"}</TableCell>
                        <TableCell>{row.Waybills ?? "-"}</TableCell>
                        <TableCell sx={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {row.UUID ?? "-"}
                        </TableCell>
                        <TableCell>{formatDate(row.Embarque?.FechaEnvio)}</TableCell>
                        <TableCell>{formatDate(row.Embarque?.FechaLlegada)}</TableCell>
                        <TableCell>{row.Embarque?.Persona ?? "-"}</TableCell>

                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                          
                            <Tooltip title="Acuse (File)">
                              <span>
                                <IconButton
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownload(row.AcuseBinario, `${row.FolioVanti}.pdf`, "application/pdf");
                                  }}
                                  disabled={!row.AcuseBinario}
                                  size="small"
                                >
                                  <Download fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Tooltip title="PDF (RutaPDF)">
                              <span>
                                <IconButton
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownload(row.PDFBinario, `${row.FolioVanti}.pdf`, "application/pdf");
                                  }}
                                  disabled={!row.PDFBinario}
                                  size="small"
                                >
                                  <PdfIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Tooltip title="XML (RutaXML)">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownload(row.XMLBinario, `${row.FolioVanti}.xml`, "application/xml");
                                  }}
                                  disabled={!row.XMLBinario}
                                >
                                  <XmlIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(row.FolioVanti);
                            }}
                            disabled={!row.AcuseBinario}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={17}>
                          <Collapse in={expanded[row.FolioVanti]} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2, p: 2, background: "#fbfdff", borderRadius: 1 }}>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle2">Información general</Typography>
                                  <Typography variant="body2">
                                    <strong>Folio:</strong> {row.FolioVanti}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Fecha emisión:</strong> {row.FechaEmision ?? "-"}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Cliente:</strong> {row.Cliente ?? "-"}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Sucursal:</strong> {row.ClienteSucursal ?? "-"}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Estatus:</strong> {row.Estatus ?? "-"}
                                  </Typography>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle2">Montos</Typography>
                                  <Typography variant="body2">
                                    <strong>Importe:</strong> {formatCurrency(row.Importe)}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Impuestos:</strong> {formatCurrency(row.Impuestos)}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Total:</strong> {formatCurrency(row.Total)}
                                  </Typography>
                                </Grid>

                                <Grid item xs={12}>
                                  <Divider sx={{ my: 1 }} />
                                  <Typography variant="subtitle2">Embarque</Typography>
                                  <Typography variant="body2">
                                    <strong>Fecha envío:</strong> {formatDate(row.Embarque?.FechaEnvio)}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Fecha llegada:</strong> {formatDate(row.Embarque?.FechaLlegada)}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Persona:</strong> {row.Embarque?.Persona ?? "-"}
                                  </Typography>
                                </Grid>

                                <Grid item xs={12}>
                                  <Divider sx={{ my: 1 }} />
                                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Artículos
                                  </Typography>

                                  <WidgetTable
                                    columns={[
                                      { field: "Articulo", header: "Artículo" },
                                      { field: "Descripcion", header: "Descripción" },
                                      { field: "Cantidad", header: "Cantidad" },
                                      {
                                        field: "Precio",
                                        header: "Precio",
                                        render: (r) =>
                                          r?.Precio != null ? Number(r.Precio).toLocaleString("es-MX", { style: "currency", currency: "MXN" }) : "-",
                                      },
                                      {
                                        field: "PrecioTotal",
                                        header: "Total",
                                        render: (r) =>
                                          r?.PrecioTotal != null ? Number(r.PrecioTotal).toLocaleString("es-MX", { style: "currency", currency: "MXN" }) : "-",
                                      },
                                    ]}
                                    rows={row.Articulos || []}
                                    emptyMessage="Este pedido no tiene artículos"
                                    getRowId={(r, idx) => r?.Articulo ?? idx}
                                  />
                                </Grid>

                                <Grid item xs={12} sx={{ textAlign: "right" }}>
                                  <Button onClick={() => handleOpenModal(row)} sx={{ mr: 1 }}>
                                    Ver en modal
                                  </Button>
                                  <Button onClick={() => toggleExpand(row.FolioVanti)}>Cerrar</Button>
                                </Grid>
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={visibleRows.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Paper>
        </>
      )}

      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>Detalle del pedido {selectedOrder?.FolioVanti}</DialogTitle>

        <DialogContent>
          {selectedOrder && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Cliente:</strong> {selectedOrder.Cliente}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Sucursal:</strong> {selectedOrder.ClienteSucursal}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Fecha:</strong> {selectedOrder.FechaEmision}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Estatus:</strong> {selectedOrder.Estatus}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Artículos
              </Typography>

              <WidgetTable
                columns={[
                  { field: "Articulo", header: "Artículo" },
                  { field: "Descripcion", header: "Descripción" },
                  { field: "Cantidad", header: "Cantidad" },
                  {
                    field: "Precio",
                    header: "Precio",
                    render: (r) => r?.Precio ? Number(r.Precio).toLocaleString("es-MX", { style: "currency", currency: "MXN" }) : "-",
                  },
                  {
                    field: "PrecioTotal",
                    header: "Total",
                    render: (r) => r?.PrecioTotal ? Number(r.PrecioTotal).toLocaleString("es-MX", { style: "currency", currency: "MXN" }) : "-",
                  },
                ]}
                rows={selectedOrder.Articulos || []}
                emptyMessage="Este pedido no tiene artículos"
              />
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseModal}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Orders;
